import React, { FunctionComponent, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  Link,
  Grid,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUserSignUpInitiate } from '../../hooks/api/auth';
import TopNav from '../../components/layout/topNav';
import GuestHeader from '../../components/layout/guest-header';
import Footer from '../../components/layout/footer';
import { ACTIVE_COLOR, INACTIVE_COLOR } from '../../components/ui/formInput';
import { IMAGES } from '../../utils/constants';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  email: ValidateField;
};

const SignUpInitiate: FunctionComponent = () => {
  const navigate = useNavigate();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>();

  const formValidation: ValidateFieldsState = {
    email: { invalid: false, error: '' },
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const { mutateAsync: signUpInitiate } = useUserSignUpInitiate();

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!email.trim()) {
      errors.email = {
        invalid: true,
        error: 'Email is required',
      };
      valid = false;
    } else if (!isValidEmail(email)) {
      errors.email = {
        invalid: true,
        error: 'Please enter a valid email address',
      };
      valid = false;
    } else {
      errors.email = { invalid: false, error: '' };
    }
    setValidateFields(errors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setError('');
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    setDisabled(true);
    try {
      const result = await signUpInitiate({ 
        email: email.toLowerCase().trim(),
      });
      if (result?.status === 200) {
        setSuccess(true)
        setTimeout(() => {
          setDisabled(false);
          setSuccess(false);
          setEmail('');
        }, 3000);
      } else {
        setDisabled(false);
        setError('An error occurred during login. Please try again.');
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
        // If user already exists, navigate to login page after showing error
        if (err.response.data.message === "User already exists") {
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        }
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during signup. Please try again.');
      }
    }
  };

  return (
    <Box 
    // sx={{ 
    //   display: "flex", 
    //   flexDirection: "column", 
    //   minHeight: "100vh" 
    // }}
    >
      <TopNav showFlag={true} />
      <GuestHeader />
      <Box
        sx={{
          backgroundColor: "#fcebed",
          padding: 4,
          mx: "auto",
          mt: 2,
          overflow: 'hidden',
         // flex: 1,
        }}
      >
        <Box sx={{
          width: '92%',
          margin: '0 auto',
          position: 'relative',
        }}>
          <img
            src={IMAGES.extranetLogo}
            alt="Logo"
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              fontSize: 280,
              color: 'rgba(0, 0, 0, 0.05)',
              zIndex: 0,
              opacity: 0.05,
              width: 'auto',
              height: '70px',
            }}
          />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Sign Up
          </Typography>

          <Divider sx={{ borderBottom: "2px solid #c8102e", mb: 2 }} />

          <Typography variant="body2" mb={1}>
            <strong>Extranet</strong>/<span style={{ color: "#c8102e" }}>Sign Up</span>
          </Typography>

          <Typography variant="body1" mt={2} mb={2} sx={{ width: '60%' }}>
            Thank you for choosing to set up an extranet account with Barksdale.
            Please provide your email address, you will then receive the form for
            your contact information. The setup process should be done in 1 to 2
            minutes.
          </Typography>

          <Typography variant="body1" mb={2}>
            If you already have an Extranet account, the system will recognize you
            and will re-direct you to the login screen.
          </Typography>

          <Typography variant="body1" mb={3}>
            If you have any questions once you are logged in, please feel free to
            contact our customer care team at{" "}
            <Link href="mailto:orders@barksdale.com" sx={{ color: "#c8102e", textDecoration: 'none' }}>
              orders@barksdale.com
            </Link>.
          </Typography>

          <Grid container spacing={2}>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Enter Email"
                variant="outlined"
                margin="normal"
                onChange={(e) => setEmail(e.target.value)}
                error={validateFields.email.invalid}
                helperText={validateFields.email.error}
                FormHelperTextProps={{ sx: { margin: '0px', backgroundColor: "#fcebed !important" } }}
                InputLabelProps={{
                  sx: {
                    color: email ? ACTIVE_COLOR : INACTIVE_COLOR,
                    "&.Mui-focused": {
                      color: ACTIVE_COLOR,
                    },
                    "&.MuiInputLabel-shrink": {
                      color: ACTIVE_COLOR,
                    },
                    "&:hover": {
                      color: ACTIVE_COLOR,
                    },
                  },
                }}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: ACTIVE_COLOR,
                    },
                    "&:hover fieldset": {
                      borderColor: ACTIVE_COLOR,
                    },
                  },
                }}
              />
            </Grid>  
          </Grid>

          <Grid size={6}>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            {
              success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  We've sent a verification email to your address. Please check your inbox and follow the instructions to complete your sign-up.
                </Alert>
              )
            }
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                sx={{ mt: 2, backgroundColor: '#c3183c' }}
                onClick={handleSubmit}
                disabled={disabled}
              >
                SUBMIT
              </Button>
              <Button
                variant="contained"
                sx={{ mt: 2, backgroundColor: '#c3183c' }}
                onClick={() => navigate("/")}
                disabled={disabled}
              >
                CANCEL
              </Button>
            </Box>
          </Grid>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default SignUpInitiate;
